import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Tooltip from '@/components/common/Tooltip';
import CategoryExplainTooltip from '@/components/transactions/CategoryExplainTooltip';
import TransactionInsightsPanel from '@/components/transactions/TransactionInsightsPanel';
import { premiumAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { getCurrencySymbol } from '@/utils/budgetHelpers';
import { searchTransactions, suggestCategory as aiSuggestCategory } from '@/services/aiService';
import { Search, Sparkles, Check, X as XIcon, Loader2, TrendingDown, TrendingUp, Lightbulb, ChevronDown, ChevronUp, Home } from 'lucide-react';
import { EXPENSE_CATEGORIES } from '@/constants/unifiedCategories';
import { toTitleCase } from '@/lib/utils';
// CATEGORY CORE SSOT: Use single source of truth for category names
import { getCategoryDisplayName, normalizeToCategoryTaxonomyId } from '@/constants/categoryCore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

// Hardcoded Income Categories (outside component for stable reference)
const INCOME_CATEGORY_OPTIONS = [
  // Salary & Employment
  { id: 'salary-wages', name: 'Salary / Wages' },
  { id: 'overtime', name: 'Overtime' },
  { id: 'bonuses', name: 'Bonuses' },
  // Business & Freelancing
  { id: 'business-income', name: 'Business Income' },
  { id: 'freelance-contract', name: 'Freelance / Contract Work' },
  { id: 'side-hustles', name: 'Side Hustles' },
  // Investments
  { id: 'dividends', name: 'Dividends' },
  { id: 'capital-gains', name: 'Capital Gains' },
  { id: 'interest-income', name: 'Interest Income' },
  // Property & Assets
  { id: 'rental-income', name: 'Rental Income' },
  { id: 'asset-sales', name: 'Asset Sales' },
  // Government & Support
  { id: 'grants', name: 'Grants' },
  { id: 'benefits-allowances', name: 'Benefits / Allowances' },
  { id: 'pension', name: 'Pension' },
  // Money Transfers
  { id: 'gifts', name: 'Gifts' },
  { id: 'family-support', name: 'Family Support' },
  { id: 'remittances', name: 'Remittances' },
  // Digital & Online Earnings
  { id: 'content-creator', name: 'Content Creator Income' },
  { id: 'affiliate-earnings', name: 'Affiliate Earnings' },
  { id: 'online-store-sales', name: 'Online Store Sales' },
  // Occasional & Other
  { id: 'lottery-betting', name: 'Lottery / Betting Wins' },
  { id: 'refunds-rebates', name: 'Refunds / Rebates' },
  { id: 'other-income', name: 'Other Income' },
];

function useMonthKey(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return [];
  const header = lines[0].split(',').map((s) => s.trim());
  const idx = (name) => header.indexOf(name);
  const iDate = idx('date');
  const iAmount = idx('amount');
  const iCategoryId = idx('categoryId');
  const iNote = idx('note');
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',');
    if (!cols || cols.length === 0) continue;
    const date = cols[iDate] || '';
    const amount = cols[iAmount] || '';
    const categoryId = iCategoryId >= 0 ? (cols[iCategoryId] || '') : '';
    const note = iNote >= 0 ? (cols[iNote] || '') : '';
    if (amount === '') continue;
    rows.push({ date, amount: Number(amount), categoryId: categoryId || null, note });
  }
  return rows;
}

/**
 * Format category ID to a proper display name
 * Uses categoryCore.js as single source of truth for consistent naming
 */
function formatCategoryName(nameOrId) {
  if (!nameOrId) return 'Unknown';
  
  // Try to get canonical name from categoryCore SSOT
  const canonicalId = normalizeToCategoryTaxonomyId(nameOrId);
  if (canonicalId) {
    const canonicalName = getCategoryDisplayName(canonicalId);
    if (canonicalName && canonicalName !== 'Unknown') {
      return canonicalName;
    }
  }
  
  // Try direct lookup
  const displayName = getCategoryDisplayName(nameOrId);
  if (displayName && displayName !== 'Unknown') {
    return displayName;
  }
  
  // Fallback: format the ID nicely
  if (nameOrId.includes('-') || nameOrId.includes('_')) {
    return nameOrId.replace(/[-_]/g, ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }
  return toTitleCase(nameOrId);
}

const TransactionsPage = () => {
  const { currency } = useAuth();
  const currencySymbol = getCurrencySymbol(currency);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  
  const [month, setMonth] = useState(useMonthKey());
  const createEmptyForm = () => ({ 
    amount: '', 
    categoryId: '', 
    date: new Date().toISOString().slice(0,10), 
    note: '',
    type: 'expense' // Default to expense
  });
  const getDateForInput = (value) => {
    if (!value) return createEmptyForm().date;
    if (typeof value === 'string' && value.length >= 10) {
      return value.slice(0, 10);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? createEmptyForm().date : parsed.toISOString().slice(0, 10);
  };

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // AI Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // AI Category Suggestion state
  const [aiCategorySuggestion, setAiCategorySuggestion] = useState(null);
  const [loadingCategorySuggestion, setLoadingCategorySuggestion] = useState(false);

  // Determine which categories to display based on type
  const isIncomeType = form.type === 'income';
  const displayCategories = isIncomeType ? INCOME_CATEGORY_OPTIONS : EXPENSE_CATEGORIES;
  
  // Debug logging - REMOVE AFTER FIXING
  console.log('🔍 DEBUG TransactionsPage:', {
    'form.type': form.type,
    'isIncomeType': isIncomeType,
    'displayCategories count': displayCategories.length,
    'first category': displayCategories[0]?.name
  });

  const isEditing = Boolean(editingId);
  const resetForm = () => {
    setForm(createEmptyForm());
    setEditingId(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [transactionsResponse, categoriesResponse] = await Promise.all([
        premiumAPI.listTransactions(month),
        premiumAPI.listCategories(),
      ]);
      console.log('🔍 TransactionsPage load - raw response:', transactionsResponse);
      const { transactions: rawTransactions } = transactionsResponse || {};
      const { categories } = categoriesResponse || {};
      console.log('🔍 TransactionsPage load - transactions:', rawTransactions);
      
      // Normalize dates - Firestore Timestamps come as { _seconds, _nanoseconds }
      const normalizedTransactions = (rawTransactions || []).map(t => ({
        ...t,
        date: t.date?._seconds 
          ? new Date(t.date._seconds * 1000).toISOString()
          : t.date
      }));
      
      setTransactions(normalizedTransactions);
      setCategories(categories || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month]);

  // AI Semantic Search
  const handleAISearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    
    setIsSearching(true);
    try {
      const results = await searchTransactions(searchQuery, 20);
      setSearchResults(results.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('AI search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  // AI Category Suggestion when note changes
  useEffect(() => {
    const getCategorySuggestion = async () => {
      if (form.note.trim().length > 3 && !form.categoryId) {
        setLoadingCategorySuggestion(true);
        try {
          const result = await aiSuggestCategory({
            description: form.note.trim(),
            ...(form.amount && { amount: parseFloat(form.amount) })
          });
          
          if (result?.suggestion?.confidence >= 0.5) {
            setAiCategorySuggestion(result.suggestion);
          } else {
            setAiCategorySuggestion(null);
          }
        } catch (error) {
          console.error('AI category suggestion error:', error);
          setAiCategorySuggestion(null);
        } finally {
          setLoadingCategorySuggestion(false);
        }
      } else {
        setAiCategorySuggestion(null);
      }
    };

    const debounce = setTimeout(getCategorySuggestion, 800);
    return () => clearTimeout(debounce);
  }, [form.note, form.amount, form.categoryId]);

  // Apply AI category suggestion
  const applyCategorySuggestion = () => {
    if (aiCategorySuggestion) {
      setForm(prev => ({ ...prev, categoryId: aiCategorySuggestion.categoryId }));
      setAiCategorySuggestion(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount),
        categoryId: form.categoryId || null,
        date: form.date,
        note: form.note,
        type: form.type || 'expense',
      };

      if (isEditing) {
        await premiumAPI.updateTransaction(editingId, payload);
      } else {
        await premiumAPI.createTransaction(payload);
      }

      // FINLIST FIX: Dispatch custom event to notify Budget page of transaction changes
      // This enables reactive updates without requiring navigation
      window.dispatchEvent(new CustomEvent('finlist:transaction-created', {
        detail: {
          type: payload.type,
          amount: payload.amount,
          categoryId: payload.categoryId,
          isEdit: isEditing
        }
      }));

      resetForm();
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const total = useMemo(() => transactions.reduce((acc, t) => acc + Number(t.amount || 0), 0), [transactions]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      for (const row of rows) {
        try {
          await premiumAPI.createTransaction(row);
        } catch (err) {
          console.error('Failed to import row', row, err);
        }
      }
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const onDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (!confirmed) return;
    try {
      if (editingId === id) {
        resetForm();
      }
      await premiumAPI.deleteTransaction(id);
      
      // FINLIST FIX: Dispatch custom event to notify Budget page of transaction deletion
      // This triggers budget recalculation to reflect the deleted transaction
      window.dispatchEvent(new CustomEvent('finlist:transaction-created', {
        detail: {
          type: 'deleted',
          transactionId: id
        }
      }));
      
      await load();
    } catch (e) { console.error(e); }
  };

  const onEdit = (transaction) => {
    setEditingId(transaction.id);
    setForm({
      amount: transaction.amount?.toString() || '',
      categoryId: transaction.categoryId || '',
      date: getDateForInput(transaction.date),
      note: transaction.note || '',
      type: transaction.type || 'expense'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Transactions</h1>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-4 py-2 text-sm font-medium text-white shadow-lg transition"
        >
          <Home size={16} />
          Home
        </Link>
      </div>

      {/* Smart Transaction Insights Panel - Phase 5 */}
      <div className="mb-6">
        <TransactionInsightsPanel onRefresh={load} />
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-10 w-full md:w-60 rounded-md border border-gray-300 px-3"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Import CSV</label>
          <input type="file" accept=".csv" onChange={onFileChange} />
          <div className="text-xs text-gray-500">Columns: date,amount,categoryId,note</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-6">
        {/* Transaction Type Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <div className="flex h-10 rounded-md border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setForm({ ...form, type: 'expense', categoryId: '' })}
              className={`flex-1 flex items-center justify-center gap-1 px-2 text-sm font-medium transition-colors ${
                form.type === 'expense'
                  ? 'bg-red-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <TrendingDown className="h-3.5 w-3.5" />
              Expense
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, type: 'income', categoryId: '' })}
              className={`flex-1 flex items-center justify-center gap-1 px-2 text-sm font-medium transition-colors ${
                form.type === 'income'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Income
            </button>
          </div>
        </div>
        <Input
          label={`Amount (${currencySymbol})`}
          type="number"
          step="0.01"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          placeholder="e.g. 12.99"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            className="h-10 w-full rounded-md border border-gray-300 px-3"
          >
            <option value="">(Select category)</option>
            {form.type === 'income' ? (
              // Income categories
              <>
                <option value="salary-wages">Salary / Wages</option>
                <option value="overtime">Overtime</option>
                <option value="bonuses">Bonuses</option>
                <option value="business-income">Business Income</option>
                <option value="freelance-contract">Freelance / Contract Work</option>
                <option value="side-hustles">Side Hustles</option>
                <option value="dividends">Dividends</option>
                <option value="capital-gains">Capital Gains</option>
                <option value="interest-income">Interest Income</option>
                <option value="rental-income">Rental Income</option>
                <option value="asset-sales">Asset Sales</option>
                <option value="grants">Grants</option>
                <option value="benefits-allowances">Benefits / Allowances</option>
                <option value="pension">Pension</option>
                <option value="gifts">Gifts</option>
                <option value="family-support">Family Support</option>
                <option value="remittances">Remittances</option>
                <option value="content-creator">Content Creator Income</option>
                <option value="affiliate-earnings">Affiliate Earnings</option>
                <option value="online-store-sales">Online Store Sales</option>
                <option value="lottery-betting">Lottery / Betting Wins</option>
                <option value="refunds-rebates">Refunds / Rebates</option>
                <option value="other-income">Other Income</option>
              </>
            ) : (
              // Expense categories from EXPENSE_CATEGORIES
              EXPENSE_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="h-10 w-full rounded-md border border-gray-300 px-3"
          />
        </div>
        <Input
          label="Note"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Optional"
        />
        <div className="flex items-end gap-3">
          <Tooltip label={isEditing ? 'Update this transaction to keep your budgets accurate.' : 'Log a new transaction and keep your budgets accurate.'}>
            <Button type="submit" loading={saving} disabled={saving || !form.amount}>
              {isEditing ? 'Save Changes' : 'Add'}
            </Button>
          </Tooltip>
          {isEditing && (
            <Button type="button" variant="ghost" onClick={resetForm} disabled={saving}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {/* AI Category Suggestion - Enhanced with Explanation */}
      {(loadingCategorySuggestion || aiCategorySuggestion) && (
        <div className="mb-6 p-4 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white dark:from-violet-900/20 dark:via-gray-800 dark:to-gray-800 dark:border-violet-800 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-900/40">
              <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
              AI Category Assistant
            </span>
          </div>
          
          {loadingCategorySuggestion ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
              <span>Analyzing your transaction...</span>
            </div>
          ) : aiCategorySuggestion && (
            <div>
              {/* Main suggestion row */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Suggested:
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded-full text-sm font-medium">
                    {aiCategorySuggestion.categoryName}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    aiCategorySuggestion.confidence >= 0.85 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : aiCategorySuggestion.confidence >= 0.70
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}>
                    {Math.round(aiCategorySuggestion.confidence * 100)}% match
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={applyCategorySuggestion}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors shadow-sm"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setAiCategorySuggestion(null)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                    Dismiss
                  </button>
                </div>
              </div>

              {/* AI Explanation Panel */}
              <CategoryExplainTooltip
                description={form.note}
                categoryId={aiCategorySuggestion.categoryId}
                categoryName={aiCategorySuggestion.categoryName}
                confidence={aiCategorySuggestion.confidence}
                amount={form.amount ? parseFloat(form.amount) : undefined}
                onSelectAlternative={(altCategoryId) => {
                  setForm(prev => ({ ...prev, categoryId: altCategoryId }));
                  setAiCategorySuggestion(null);
                }}
              />
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="mb-3 text-gray-700">Total this month: <span className="font-semibold">£{total.toFixed(2)}</span></div>
          <div className="grid gap-2">
            {transactions.length === 0 && (
              <div className="text-gray-500">No transactions for {month}</div>
            )}
            {transactions.map((t) => {
              const isIncome = t.type === 'income';
              const categoryList = isIncome ? INCOME_CATEGORY_OPTIONS : EXPENSE_CATEGORIES;
              const rawCategoryName = categoryList.find(c => c.id === t.categoryId)?.name 
                || categories.find(c => c.id === t.categoryId)?.name 
                || t.categoryId;
              const categoryName = formatCategoryName(rawCategoryName);
              
              return (
                <div key={t.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between bg-white border rounded p-3">
                  <div>
                    <div className="flex items-center gap-2 font-medium">
                      {isIncome ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                      <span className={isIncome ? 'text-green-600' : 'text-red-600'}>
                        {isIncome ? '+' : '-'}£{Number(t.amount).toFixed(2)}
                      </span>
                      {t.categoryId && (
                        <span className="text-gray-500 font-normal">({categoryName})</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{new Date(t.date).toLocaleDateString()} • {t.note || t.id}</div>
                  </div>
                  <div className="flex gap-2">
                    <Tooltip label="Edit this transaction to correct the amount or category.">
                      <Button variant="outline" size="sm" onClick={() => onEdit(t)}>
                        Edit
                      </Button>
                    </Tooltip>
                    <Tooltip label="Delete this transaction. This action cannot be undone.">
                      <Button variant="destructive" size="sm" onClick={() => onDelete(t.id)}>
                        Delete
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {importing && <div className="mt-3 text-sm text-gray-500">Importing…</div>}
      
      {/* Confirm Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default TransactionsPage;
